import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { uploadMediaFile } from '@/hooks/useUpload';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import {
  fetchCommerceCategories,
  createCommerceCategory,
  updateCommerceCategory,
  setCommerceCategoryVisible,
  deleteCommerceCategory,
  reorderCommerceCategories,
  migrateVendorsOnReparent,
  fetchCommerceCategoryVendorCounts,
  COMMERCE_CATEGORY_TYPES,
  type CommerceCategory,
  type CommerceCategoryType,
  type CategoryVendorCounts,
} from '@/lib/marketplaceCategories';

// ─────────────────────────────────────────────────────────────
// Gestion des catégories de la marketplace (dashboard propriétaire).
// Créer / modifier / réordonner / masquer — strictement isolé par commerce.
// ─────────────────────────────────────────────────────────────

const INPUT =
  'w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors';

interface FormState {
  id: number | null;
  nom: string;
  type: CommerceCategoryType;
  description: string;
  icon: string;
  image: string;
  parentId: number | null;
}

const EMPTY: FormState = {
  id: null,
  nom: '',
  type: 'produits',
  description: '',
  icon: '',
  image: '',
  parentId: null,
};

const ICON_SUGGESTIONS = [
  'ri-price-tag-3-line',
  'ri-shopping-bag-3-line',
  'ri-service-line',
  'ri-megaphone-line',
  'ri-home-4-line',
  'ri-smartphone-line',
  'ri-women-line',
  'ri-shirt-line',
  'ri-restaurant-2-line',
  'ri-car-line',
  'ri-briefcase-line',
  'ri-heart-3-line',
];

export default function CategoryManager({ commerceId }: { commerceId: number }) {
  const [type, setType] = useState<CommerceCategoryType>('produits');
  const [categories, setCategories] = useState<CommerceCategory[]>([]);
  const [vendorCounts, setVendorCounts] = useState<CategoryVendorCounts>({ byCategory: {}, bySubcategory: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<CommerceCategory | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!commerceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [cats, counts] = await Promise.all([
        fetchCommerceCategories(commerceId),
        fetchCommerceCategoryVendorCounts(commerceId),
      ]);
      setCategories(cats);
      setVendorCounts(counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [commerceId]);

  useEffect(() => {
    load();
  }, [load]);

  // Catégories du type sélectionné (racines + enfants).
  const scoped = useMemo(
    () => categories.filter((c) => (c.type || 'autres') === type),
    [categories, type],
  );
  const roots = useMemo(() => scoped.filter((c) => !c.parent_id), [scoped]);
  const childrenOf = useMemo(
    () => (id: number) => scoped.filter((c) => c.parent_id === id),
    [scoped],
  );

  // Arbre aplati (racines puis leurs descendants) avec profondeur — sert au
  // sélecteur « catégorie parente » pour visualiser la hiérarchie (3 niveaux+).
  const flatTree = useMemo(() => {
    const out: Array<{ cat: CommerceCategory; depth: number }> = [];
    const walk = (nodes: CommerceCategory[], depth: number) => {
      nodes.forEach((n) => {
        out.push({ cat: n, depth });
        walk(childrenOf(n.id), depth + 1);
      });
    };
    walk(roots, 0);
    return out;
  }, [roots, childrenOf]);

  // Identifiants de la catégorie éditée et de tous ses descendants : on ne peut
  // pas se choisir soi-même (ni un descendant) comme parent — évite les cycles.
  const selfAndDescendants = useMemo(() => {
    const set = new Set<number>();
    if (form.id == null) return set;
    const walk = (id: number) => {
      childrenOf(id).forEach((k) => {
        if (!set.has(k.id)) {
          set.add(k.id);
          walk(k.id);
        }
      });
    };
    set.add(form.id);
    walk(form.id);
    return set;
  }, [form.id, childrenOf]);

  // Catégorie parente choisie dans le formulaire (pour le libellé du niveau).
  const parentEntry = form.parentId != null
    ? flatTree.find((e) => e.cat.id === form.parentId)
    : undefined;
  const childLevelLabel = parentEntry
    ? (parentEntry.depth === 0 ? 'Sous-catégorie' : 'Sous-sous-catégorie')
    : null;

  const openCreate = (parent: CommerceCategory | null = null) => {
    setForm({ ...EMPTY, type, parentId: parent ? parent.id : null });
    setOpen(true);
  };

  const openEdit = (c: CommerceCategory) => {
    setForm({
      id: c.id,
      nom: c.nom,
      type: (c.type as CommerceCategoryType) || type,
      description: c.description || '',
      icon: c.icon || '',
      image: c.image_couverture || '',
      parentId: c.parent_id,
    });
    setOpen(true);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadMediaFile(file, 'categories');
      setForm((f) => ({ ...f, image: url }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async () => {
    if (!form.nom.trim()) {
      setError('Le nom de la catégorie est obligatoire.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (form.id) {
        await updateCommerceCategory(form.id, commerceId, {
          nom: form.nom,
          type: form.type,
          description: form.description,
          icon: form.icon,
          image_couverture: form.image,
          parentId: form.parentId,
        });
        // La catégorie devient une sous-catégorie : les vendeurs qui l'avaient
        // comme catégorie principale héritent du changement (catégorie
        // principale = nouveau parent, sous-catégorie = cette catégorie).
        if (form.parentId != null) {
          await migrateVendorsOnReparent(commerceId, form.id, form.parentId);
        }
      } else {
        await createCommerceCategory({
          commerceId,
          nom: form.nom,
          type: form.type,
          description: form.description,
          icon: form.icon,
          image_couverture: form.image,
          parentId: form.parentId,
        });
      }
      setOpen(false);
      setForm(EMPTY);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = async (c: CommerceCategory) => {
    const next = Number(c.afficher) === 1 ? 0 : 1;
    setBusy(c.id);
    setCategories((prev) => prev.map((x) => (x.id === c.id ? { ...x, afficher: next } : x)));
    try {
      await setCommerceCategoryVisible(c.id, commerceId, next === 1);
    } catch {
      setCategories((prev) => prev.map((x) => (x.id === c.id ? { ...x, afficher: c.afficher } : x)));
      setError('Impossible de mettre à jour la visibilité.');
    } finally {
      setBusy(null);
    }
  };

  const move = async (list: CommerceCategory[], index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= list.length) return;
    const reordered = [...list];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    // Applique le nouvel ordre localement (racines du type courant).
    const orderMap = new Map(reordered.map((c, i) => [c.id, i + 1]));
    setCategories((prev) =>
      prev.map((c) => (orderMap.has(c.id) ? { ...c, sort_order: orderMap.get(c.id) as number } : c)),
    );
    try {
      await reorderCommerceCategories(commerceId, reordered.map((c) => c.id));
    } catch {
      setError('Impossible de réordonner les catégories.');
      await load();
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const target = deleting;
    setDeleting(null);
    setCategories((prev) => prev.filter((c) => c.id !== target.id));
    try {
      await deleteCommerceCategory(target.id, commerceId);
    } catch {
      setError('Impossible de supprimer la catégorie.');
      await load();
    }
  };

  const renderRow = (c: CommerceCategory, siblings: CommerceCategory[], index: number, depth = 0) => {
    const img = resolveMediaUrl(c.image_couverture);
    const hidden = Number(c.afficher) !== 1;
    const kids = childrenOf(c.id);
    // Vendeurs rattachés à cette catégorie (comme catégorie principale ou
    // comme sous-catégorie) : aide à repérer celles à réorganiser.
    const vendorCount =
      (vendorCounts.byCategory[c.id] || 0) + (vendorCounts.bySubcategory[c.id] || 0);
    const levelLabel = depth === 0 ? null : depth === 1 ? 'Sous-catégorie' : 'Sous-sous-catégorie';
    return (
      <div key={c.id} className={depth > 0 ? 'ml-6 border-l border-background-200/70 pl-3' : ''}>
        <div className={`group bg-background-50 border border-background-200/70 rounded-lg p-3 flex items-center gap-3 ${hidden ? 'opacity-60' : ''}`}>
          {/* Poignée / ordre (à chaque niveau, agit sur les frères) */}
          <div className="flex flex-col items-center gap-0.5">
            <button
              type="button"
              onClick={() => move(siblings, index, -1)}
              disabled={index === 0}
              className="w-6 h-5 flex items-center justify-center rounded text-foreground-400 hover:text-foreground-800 hover:bg-background-100 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
              title="Monter"
            >
              <i className="ri-arrow-up-s-line text-sm"></i>
            </button>
            <button
              type="button"
              onClick={() => move(siblings, index, 1)}
              disabled={index === siblings.length - 1}
              className="w-6 h-5 flex items-center justify-center rounded text-foreground-400 hover:text-foreground-800 hover:bg-background-100 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
              title="Descendre"
            >
              <i className="ri-arrow-down-s-line text-sm"></i>
            </button>
          </div>

          <div className="w-11 h-11 rounded-lg bg-background-100 overflow-hidden flex items-center justify-center flex-shrink-0">
            {img ? (
              <img src={img} alt={c.nom} className="w-full h-full object-cover object-top" />
            ) : (
              <i className={`${c.icon || 'ri-price-tag-3-line'} text-lg text-foreground-400`}></i>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-foreground-900 truncate">{c.nom}</h3>
              {levelLabel && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-100 text-foreground-500 whitespace-nowrap">
                  {levelLabel}
                </span>
              )}
              {hidden && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-foreground-900/80 text-background-50 whitespace-nowrap">
                  Masquée
                </span>
              )}
              {childrenOf(c.id).length > 0 && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-secondary-100 text-secondary-900 whitespace-nowrap">
                  {childrenOf(c.id).length} sous-cat.
                </span>
              )}
              {vendorCount > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent-100 text-accent-800 whitespace-nowrap"
                  title={`${vendorCount} vendeur${vendorCount > 1 ? 's' : ''} rattaché${vendorCount > 1 ? 's' : ''} à cette catégorie`}
                >
                  <i className="ri-store-2-line text-[10px]"></i>
                  {vendorCount} vendeur{vendorCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
            {c.description && <p className="text-xs text-foreground-500 line-clamp-1 mt-0.5">{c.description}</p>}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => openCreate(c)}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer"
              title="Ajouter une catégorie enfant"
            >
              <i className="ri-add-line"></i>
            </button>
            <button
              type="button"
              onClick={() => toggleVisible(c)}
              disabled={busy === c.id}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-50"
              title={hidden ? 'Afficher' : 'Masquer'}
            >
              <i className={busy === c.id ? 'ri-loader-4-line animate-spin' : hidden ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
            </button>
            <button
              type="button"
              onClick={() => openEdit(c)}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer"
              title="Modifier"
            >
              <i className="ri-edit-line"></i>
            </button>
            <button
              type="button"
              onClick={() => setDeleting(c)}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
              title="Supprimer"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>
        </div>

        {kids.length > 0 && (
          <div className="mt-2 space-y-2">
            {kids.map((k, i) => renderRow(k, kids, i, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          {COMMERCE_CATEGORY_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setType(t.value)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                type === t.value ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              <i className={t.icon}></i>
              {t.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => openCreate(null)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer w-fit"
        >
          <i className="ri-add-line"></i>
          Nouvelle catégorie
        </button>
      </div>

      <p className="text-xs text-foreground-500 mb-4 flex items-start gap-2">
        <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
        <span>
          Ces catégories appartiennent uniquement à votre marketplace. Vos vendeurs y rangent leurs
          produits, services et annonces, et elles s&apos;affichent sur votre site public.
        </span>
      </p>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : roots.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-price-tag-3-line text-2xl text-foreground-400"></i>
          </div>
          <h3 className="text-base font-semibold text-foreground-900 mb-1">Aucune catégorie</h3>
          <p className="text-sm text-foreground-500 max-w-xs mb-5">
            Créez vos premières catégories pour organiser les contenus de votre marketplace.
          </p>
          <button
            type="button"
            onClick={() => openCreate(null)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Créer une catégorie
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {roots.map((c, i) => renderRow(c, roots, i))}
        </div>
      )}

      {/* Formulaire */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : () => setOpen(false)} />
          <form
            onSubmit={(e) => { e.preventDefault(); save(); }}
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6"
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold font-heading text-foreground-950">
                {form.id
                  ? 'Modifier la catégorie'
                  : parentEntry && childLevelLabel
                    ? `${childLevelLabel} de : ${parentEntry.cat.nom}`
                    : 'Nouvelle catégorie'}
              </h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={saving}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                  className={INPUT}
                  placeholder="Ex : Immobilier"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value as CommerceCategoryType })}
                    className={`${INPUT} cursor-pointer`}
                  >
                    {COMMERCE_CATEGORY_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Catégorie parente</label>
                  <select
                    value={form.parentId ?? ''}
                    onChange={(e) => setForm({ ...form, parentId: e.target.value ? Number(e.target.value) : null })}
                    className={`${INPUT} cursor-pointer`}
                  >
                    <option value="">Aucune (racine)</option>
                    {flatTree
                      .filter(({ cat }) => !selfAndDescendants.has(cat.id))
                      .map(({ cat, depth }) => (
                        <option key={cat.id} value={cat.id}>
                          {depth > 0 ? `${'— '.repeat(depth)}` : ''}{cat.nom}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className={`${INPUT} resize-none`}
                  placeholder="Courte description (optionnel)"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Icône</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {ICON_SUGGESTIONS.map((ic) => (
                    <button
                      key={ic}
                      type="button"
                      onClick={() => setForm({ ...form, icon: ic })}
                      className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
                        form.icon === ic ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                      }`}
                    >
                      <i className={ic}></i>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Image de couverture</label>
                <div className="flex items-center gap-3">
                  {form.image ? (
                    <div className="w-16 h-16 rounded-lg overflow-hidden border border-background-200/70 flex-shrink-0">
                      <img src={resolveMediaUrl(form.image)} alt="" className="w-full h-full object-cover object-top" />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-image-line text-foreground-300"></i>
                    </div>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="px-3.5 py-2 rounded-lg border border-background-200/70 bg-background-50 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
                  >
                    <i className={uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-line'}></i>
                    {uploading ? 'Upload...' : form.image ? "Changer l'image" : 'Choisir une image'}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={saving}
                  className="flex-1 py-2.5 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving || uploading}
                  className="flex-1 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Enregistrement...</>
                  ) : (
                    <><i className="ri-save-line"></i>{form.id ? 'Enregistrer' : 'Créer'}</>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Confirmation suppression */}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleting(null)} />
          <div className="relative w-full max-w-sm bg-background-50 border border-background-200/70 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-500 text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-950">Supprimer la catégorie ?</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Les contenus liés ne seront plus classés.</p>
              </div>
            </div>
            <p className="text-xs text-foreground-600 mb-5">
              <strong>{deleting.nom}</strong> sera définitivement supprimée.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleting(null)}
                className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-delete-bin-line"></i>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}